import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, PaletteIcon, ScissorsIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/c9415f6a-f635-4dea-a6bf-9bdc54b57875/1792556931794_kk-mockup-fad1b6b1.webp';
export const heroGuarantees = ['Umiddelbar digital nedlasting', 'Engangsbetaling', 'Ingen abonnement', '7 dagers fornøydgaranti'];
export const stats = [{
  value: '240',
  label: 'Sider i hovedbindene'
}, {
  value: '6',
  label: 'Digitale bind'
}, {
  value: '10',
  label: 'Trinn – fra 1. til 10.'
}, {
  value: '5',
  label: 'Bonuser inkludert'
}, {
  value: '3',
  label: 'Nivåer på arbeidsarkene'
}];
export const differenceStats = [{
  value: '1.–10. trinn',
  label: 'Fra barneskole til ungdomsskole'
}, {
  value: '6 digitale bind',
  label: 'Fra årsplan og teknikker til design, kunsthistorie og vurdering'
}, {
  value: 'PDF klar til utskrift',
  label: 'Bruk det digitalt eller skriv ut til kunstrommet'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Kvelder og helger forsvinner',
  text: 'Timer foran skjermen for å planlegge neste ukes kunst og håndverk – og du er fortsatt ikke ferdig.'
}, {
  title: 'Timer med leting på Pinterest',
  text: 'Ideer fra ulike nettsider er for enkle, for vanskelige eller må tilpasses før de fungerer i klassen.'
}, {
  title: 'Klasser med store nivåforskjeller',
  text: 'Noen elever trenger mye støtte, andre er ferdige på ti minutter – og økta skal fungere for alle.'
}, {
  title: 'Faget uten fagbakgrunn',
  text: 'Du er kontaktlærer og har fått kunst og håndverk på timeplanen, men ingen har vist deg hvordan.'
}, {
  title: 'Vanskelig å vurdere kreativt arbeid',
  text: 'Kompetansemål, underveisvurdering og karakterer – hvordan begrunner du det, og hvordan dokumenterer du det?'
}, {
  title: 'Stress når timen begynner',
  text: 'Når tiden ikke strekker til, blir det fort tegning på fri hånd og improvisasjon i siste liten.'
}];
export const afterItems = ['En ferdig årsplan for 1.–10. trinn', '12 ferdige økter minutt for minutt', 'Materialliste og lærerinstruksjon til hver økt', 'Arbeidsark i 3 nivåer', 'Teknikker steg for steg – tegning, maling og håndverk', 'Koblet til kompetansemål i LK20', 'Kunsthistorie og bildeanalyse i 4 steg', 'Vurderingskriterier og ferdige tilbakemeldinger', 'PDF klar til utskrift'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Kveldene går med til planlegging',
  text: 'Bruker du kvelder og helger på å finne på neste ukes kunst- og håndverkstimer?'
}, {
  icon: FolderSearchIcon,
  title: 'Du vet ikke hvilket opplegg du skal velge',
  text: 'Leter du hele tiden på nettet etter oppgaver som passer trinnet og kompetansemålene?'
}, {
  icon: UsersIcon,
  title: 'Klassen har store nivåforskjeller',
  text: 'Trenger du oppgaver som fungerer både for elever som trenger støtte og for dem som vil mer?'
}, {
  icon: PaletteIcon,
  title: 'Du underviser uten fagbakgrunn',
  text: 'Har du fått faget på timeplanen uten å ha studert kunst og håndverk selv?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Vurderingen er vanskelig',
  text: 'Vil du kunne vise elever og foresatte hva som er forventet – og begrunne vurderingen?'
}, {
  icon: LayoutGridIcon,
  title: 'Du trenger en tydeligere struktur',
  text: 'Vil du ha alt samlet, slik at du raskt finner riktig økt når timen begynner?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: '1.–10. trinn',
  title: 'Årsplan & 12 ferdige økter',
  tagline: 'Vit nøyaktig hva hver time skal handle om – hele skoleåret.',
  intro: 'Det første bindet gir deg en komplett årsplan og tolv ferdige økter som du kan gjennomføre med en gang.',
  items: ['Årsplan for 1.–4., 5.–7. og 8.–10. trinn', '38 skoleuker koblet til kompetansemål', '12 ferdige økter minutt for minutt', 'Økter på 60 og 90 minutter', 'Materialliste til hver økt', 'Lærerinstruksjon steg for steg', 'Fargelære og form som rød tråd'],
  outro: 'Ferdige økter som gjør at du går inn i kunstrommet med en tydelig plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: '1.–10. trinn',
  title: 'Tegning, maling & bildefortelling',
  tagline: 'Lær bort teknikkene med tydelige steg.',
  intro: 'Det andre bindet går gjennom tegning og maling steg for steg, med oppgaver som passer hvert nivå.',
  items: ['Tegneteknikker', 'Perspektiv', 'Fargeblanding', 'Maleteknikker', 'Illustrasjon', 'Tegneserie og bildefortelling', 'Arbeidsark i 3 nivåer'],
  outro: 'En tydelig støtte for å lære bort teknikk på en enkel og konsekvent måte.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '1.–10. trinn',
  title: 'Håndverk & materialer',
  tagline: 'Trygt og strukturert arbeid med ekte materialer.',
  intro: 'Det tredje bindet samler håndverksteknikkene med verktøy, sikkerhet og faste rutiner i verkstedet.',
  items: ['Tekstil: søm, veving og toving', 'Leire', 'Tre og spikking', 'Papir og papp', 'Trykk', 'Teknikk steg for steg', 'Verktøy og HMS/sikkerhet', 'Verkstedrutiner'],
  outro: 'Ferdige opplegg som holder elevene aktive og verkstedet i orden.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: '1.–10. trinn',
  title: 'Design & visuell kommunikasjon',
  tagline: 'Fra idé til ferdig produkt.',
  intro: 'Det fjerde bindet viser designprosessen og hvordan bilder og symboler kommuniserer.',
  items: ['Designprosessen: idé → skisse → modell → produkt', 'Plakat', 'Logo og symbol', 'Typografi', 'Arkitektur og produktdesign', 'Bilder i media og reklame', 'Digitale bilder'],
  outro: 'En praktisk støtte for å la elevene tenke, skissere og skape som designere.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: '1.–10. trinn',
  title: 'Kulturforståelse & kunsthistorie',
  tagline: 'Norsk kunst, samisk duodji og kunst fra hele verden.',
  intro: 'Det femte bindet gir elevene møter med kunst og kunsthåndverk – og verktøy for å snakke om det de ser.',
  items: ['Norsk kunst og kunsthåndverk', 'Munch, Kittelsen og Astrup', 'Rosemaling og treskjæring', 'Samisk duodji', 'Kunstnere fra hele verden', 'Bildeanalyse i 4 steg', 'Egne uttrykk og identitet'],
  outro: 'Kulturforståelse som blir konkret og praktisk i klasserommet.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: '1.–10. trinn',
  title: 'Vurdering & prosjekter',
  tagline: 'Vis hva elevene kan – og hva neste steg er.',
  intro: 'Det sjette bindet gir deg verktøyene for underveisvurdering, tydelige kriterier og tverrfaglige prosjekter.',
  items: ['Kompetansemål forklart', 'Underveisvurdering', 'Vurderingskriterier i 3 nivåer', 'Egenvurdering og kameratvurdering', '6 tverrfaglige prosjekter', 'Prøver A/B', '100 ferdige tilbakemeldinger'],
  outro: 'En komplett støtte for å vurdere kreativt arbeid på en rettferdig og begrunnet måte.',
  note: 'Materialet er selvstendig utviklet og er ikke et offisielt produkt fra Utdanningsdirektoratet.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitale bind for kunst og håndverk',
  text: 'Et komplett undervisningssystem som hjelper deg å planlegge og gjennomføre faget hele skoleåret. Hvert bind har 40 sider med ferdige økter, arbeidsark og materiell til kunstrommet.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Ferdige økter minutt for minutt',
  text: 'Slipp å begynne på et tomt ark. Tydelige opplegg med tider, materialer og lærerinstruksjon hjelper deg å komme raskt i gang.'
}, {
  icon: FileTextIcon,
  title: 'Arbeidsark i 3 nivåer',
  text: 'Samme oppgave i tre nivåer, slik at alle elever kan jobbe med det samme – på sitt nivå.'
}, {
  icon: ScissorsIcon,
  title: 'Teknikker & materialer',
  text: 'Tegning, maling, tekstil, leire, tre og trykk forklart steg for steg, med verktøy og sikkerhet.'
}, {
  icon: LayersIcon,
  title: 'Koblet til LK20',
  text: 'Kjerneelementene og kompetansemålene etter 2., 4., 7. og 10. trinn – tydelig koblet til øktene.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Vurdering & tilbakemeldinger',
  text: 'Vurderingskriterier, egenvurdering og 100 ferdige tilbakemeldinger som støtte i underveisvurderingen.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplan',
  text: 'Støtte for å organisere hele skoleåret og velge temaer ut fra trinnet og klassen.'
}];
// Real pages from Bind 1. While empty, InsidePages shows a neutral placeholder.
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [];
export const pageTypes = ['Ferdige økter', 'Arbeidsark i 3 nivåer', 'Teknikker steg for steg', 'Vurdering'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vikarpakken – 12 kunsttimer med enkle materialer',
  text: 'Tolv ferdige timer som en vikar kan gjennomføre uten forberedelse og med materialer som finnes på skolen.',
  items: ['12 ferdige timer', 'Enkle materialer', 'Instruksjon til vikaren', 'Klar til utskrift'],
  benefit: 'Ingen bortkastede timer når du er borte.'
}, {
  title: 'Hurtigstart – 30 kreative oppvarminger',
  text: 'Korte oppvarminger som får elevene i gang med å skape fra første minutt.',
  items: ['30 oppvarminger', '5–10 minutter', 'Tegning, form og farge', 'For alle trinn'],
  benefit: 'En rolig og kreativ start på hver time.'
}, {
  title: 'Visuell støtte i kunstrommet',
  text: 'Plakater og visuelle kort som hjelper elevene å forstå steg, rutiner og begreper.',
  items: ['Stegkort', 'Rutiner i verkstedet', 'Begreper og fargesirkel', 'Klar til utskrift'],
  benefit: 'Mindre forklaring – mer tid til å skape.'
}, {
  title: 'Materialsmart – skap med lite budsjett',
  text: 'Ideer og opplegg som bruker gjenbruk og rimelige materialer.',
  items: ['Gjenbruksmaterialer', 'Rimelige alternativer', 'Opplegg med lite utstyr', 'Tips til innkjøp'],
  benefit: 'Gode kunsttimer – selv med et lite budsjett.'
}, {
  title: 'Klassens kunstutstilling',
  text: 'En steg-for-steg-guide til å planlegge og vise frem elevenes arbeider.',
  items: ['Planlegging steg for steg', 'Skilt og tekster', 'Invitasjon til foresatte', 'Refleksjon etterpå'],
  benefit: 'Elevenes arbeid blir sett og verdsatt.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Åpne KUNSTKLAR™',
  text: 'Last ned pakken og åpne bindet som passer trinnet ditt.'
}, {
  title: 'Velg trinn og økt',
  text: 'Finn riktig økt, teknikk eller arbeidsark for klassen din.'
}, {
  title: 'Forbered deg på 5 minutter',
  text: 'Les gjennom lærerinstruksjonen og materiallisten og gjør klart det du trenger.'
}, {
  title: 'Skriv ut og start timen',
  text: 'Skriv ut arbeidsarkene eller bruk materialet digitalt – og gå inn i timen med en tydelig plan.'
}];
export const audienceItems = ['Underviser i kunst og håndverk på 1.–10. trinn', 'Er kontaktlærer og har fått faget uten fagbakgrunn', 'Vil ha en ferdig årsplan koblet til LK20', 'Har klasser med store nivåforskjeller', 'Vil slippe å planlegge hver time fra bunnen av', 'Vil ha tydelige teknikker og materiallister', 'Vil gjøre vurderingen enklere og mer begrunnet', 'Vil spare kvelder og helger på planlegging'];
export const timeValueTasks = ['Lete etter ideer på nettet', 'Planlegge øktene', 'Lage arbeidsark og maler', 'Skrive materiallister', 'Vurdere og gi tilbakemeldinger'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Årsplan, tegning og maling, håndverk, design, kunsthistorie og vurdering'
}, {
  label: '5 bonuser uten ekstra kostnad',
  detail: 'Vikarpakken, Hurtigstart, Visuell støtte, Materialsmart og Klassens kunstutstilling'
}, {
  label: 'For 1.–10. trinn',
  detail: 'Ferdige økter, arbeidsark i 3 nivåer og vurderingskriterier'
}];
export const offerIncludes = ['Umiddelbar digital nedlasting', 'PDF-filer du kan lagre', 'Klar til utskrift', 'Ingen fysisk vare sendes'];
export const PRICE = '299 kr';
export const PRICE_BEFORE = '599 kr';

// Only add authentic reviews of KUNSTKLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hvordan får jeg materialet?',
  a: 'KUNSTKLAR™ leveres digitalt. Etter kjøpet får du tilgang til PDF-filer som du kan laste ned og lagre. Du kan bruke materialet på PC, nettbrett eller mobil og skrive ut de sidene du trenger.'
}, {
  q: 'Er det tilpasset LK20?',
  a: 'Ja. Årsplanen og øktene er koblet til kjerneelementene og kompetansemålene i kunst og håndverk etter 2., 4., 7. og 10. trinn. KUNSTKLAR™ er et selvstendig undervisningsmateriell og ikke et offisielt produkt fra Utdanningsdirektoratet.'
}, {
  q: 'Hvilke trinn passer det for?',
  a: 'Materialet dekker 1.–10. trinn, med årsplaner for 1.–4., 5.–7. og 8.–10. trinn. Du velger økter og oppgaver ut fra trinnet og klassen din.'
}, {
  q: 'Jeg har ikke fagbakgrunn i kunst og håndverk. Passer det for meg?',
  a: 'Ja. Hver økt har materialliste og lærerinstruksjon steg for steg, og teknikkene er forklart slik at du kan gjennomføre dem selv om du ikke har studert faget.'
}, {
  q: 'Kan jeg virkelig forberede en økt på 5 minutter?',
  a: 'KUNSTKLAR™ er laget for å gjøre forberedelsene raskere. Du åpner et bind, velger en ferdig økt og leser gjennom den på omtrent fem minutter. Tiden kan variere ut fra økta, materialene og behovet for tilpasning.'
}, {
  q: 'Holder materialet for et helt skoleår?',
  a: 'KUNSTKLAR™ inneholder en årsplan for 38 skoleuker, 12 ferdige økter og opplegg i alle bindene. Du velger, kombinerer og tilpasser materialet etter din plan og elevenes behov.'
}, {
  q: 'Er det arbeidsark i flere nivåer?',
  a: 'Ja. Arbeidsarkene finnes i 3 nivåer, slik at elever med ulike behov kan jobbe med samme oppgave.'
}, {
  q: 'Er vurdering inkludert?',
  a: 'Ja. Bind 6 inneholder kompetansemål forklart, underveisvurdering, vurderingskriterier i 3 nivåer, egenvurdering, kameratvurdering, prøver A/B og 100 ferdige tilbakemeldinger.'
}, {
  q: 'Må jeg skrive ut alt?',
  a: 'Nei. Du velger selv hvilke sider du skriver ut, og du kan også bruke deler av materialet digitalt.'
}, {
  q: 'Er de fem bonusene inkludert?',
  a: `Ja. Alle fem bonusene er inkludert i pakken uten ekstra kostnad – alt for ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nei. Du betaler én gang og får tilgang til det digitale materialet. Ingen faste trekk.'
}, {
  q: 'Kan jeg bruke materialet på mobil eller nettbrett?',
  a: 'Ja. PDF-filene kan åpnes på PC, nettbrett og mobil. For å se på arbeidsarkene før utskrift anbefaler vi PC eller nettbrett.'
}, {
  q: 'Finnes det en garanti?',
  a: 'Ja. KUNSTKLAR™ har 7 dagers fornøydgaranti. Hvis materialet ikke svarer til forventningene dine, kan du kontakte oss og be om å få pengene tilbake innen garantiperioden, i henhold til vilkårene.'
}];
export const footerLinks = ['Kjøpsvilkår', 'Personvern', 'Refusjon', 'Kontakt'];
